import { forwardRef, useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import { motion } from 'framer-motion';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import { useTranslation } from 'react-i18next';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';

import OfferItem from '../../../OfferItem/OfferItem';
import { useCheckout } from '../../../../hooks/use-checkout';
import { SummaryLayout } from '../SummaryLayout/SummaryLayout';
import { usePaymentState } from '../../hooks/use-payment-state';
import { useAppConfig } from '../../../../hooks/use-app-config';
import { FormattedPrice } from '../FormattedPrice/FormattedPrice';
import { useDeviceHelper } from '../../../../hooks/use-device-helper';
import { PriceBreakdown } from './components/PriceBreakdown/PriceBreakdown';
import { getIsSupercellTemplate } from '../../../../utils/get-is-supercell-template';
import { getCheckoutCustomization } from '../../../../utils/get-checkout-customization';
import { getIsNewMobileLandscape } from '../../../../utils/get-is-new-mobile-landscape';
import { DescriptionSkeleton } from './components/DescriptionSkeleton/DescriptionSkeleton';
import { SubscriptionsDescription } from './components/SubscriptionsDescriptions/SubscriptionsDescriptions';

import './style.scss';

const IS_SUPERCELL_TEMPLATE = getIsSupercellTemplate();
const IS_NEW_MOBILE_LANDSCAPE = getIsNewMobileLandscape();

export const Description = forwardRef<HTMLDivElement, { isSubscription?: boolean }>(
  function Description({ isSubscription = false }, ref) {
    const { checkoutData, isCheckoutDataLoading, subscriptionData } = useCheckout();
    const { couponsConfiguration } = useAppConfig();
    const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
    const { theme, featureFlags } = useAppConfig();
    const { isMobile: isMobileDimensions, isMobileLandscape } = useDeviceHelper();

    const isMobileCollapsedBreakdown = featureFlags?.checkout_mobile_collapsed_breakdown;

    const isMobile = IS_NEW_MOBILE_LANDSCAPE
      ? isMobileDimensions || isMobileLandscape
      : isMobileDimensions;
    const imgUrl = (isMobile ? theme?.mobileBackgroundImage : theme?.backgroundImage) || '';
    const [sectionStyle, setSectionStyle] = useState<any>({
      backgroundColor: imgUrl ? '#cacbd4' : theme?.primary,
      // backgroundImage: `url(${isMobile ? theme?.mobileBackgroundImage : theme?.backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      color: theme?.text,
      // transition: 'background 0.3s ease-in-out',
    });
    const [expanded, setExpanded] = useState(getCheckoutCustomization().expandProductList);
    const [isBackgroundLoaded, setIsBackgroundLoaded] = useState(false);
    const priceBreakdown = usePaymentState((state) => state.priceBreakdown);

    const handleExpandClick = () => {
      setExpanded(!expanded);
    };
    const { items, pricing, offer } = checkoutData || {};
    const showCouponField = couponsConfiguration?.enabled;
    const priceNet = priceBreakdown?.baseNet;
    const priceTotal = priceBreakdown?.priceTotal;
    const tax = priceBreakdown?.tax;
    const discount = priceBreakdown?.discount;
    const isOfferItemsExists = Boolean(items?.length);
    const showDetailsSection =
      isMobile && ((!isCheckoutDataLoading && isOfferItemsExists) || isMobileCollapsedBreakdown);
    const showTaxValue = priceBreakdown?.isTaxValid;
    const offerDisplayName = offer?.displayName || offer?.name || '';
    const subscriptionDisplayName = subscriptionData?.plan?.description || '';

    useEffect(() => {
      if (IS_SUPERCELL_TEMPLATE) {
        setSectionStyle({
          backgroundColor: '#ffffff',
          color: '#000000',
        });
        setIsBackgroundLoaded(true);
        return;
      }

      const imgUrl = (isMobile ? theme?.mobileBackgroundImage : theme?.backgroundImage) || '';

      // If no image URL, set fallback style immediately
      if (!imgUrl) {
        const fallbackStyle = {
          backgroundColor: theme?.primary,
          color: theme?.text,
        };
        setSectionStyle(fallbackStyle);
        setIsBackgroundLoaded(true);
        console.log('no img url');
        return;
      }

      const img = new Image();
      img.src = imgUrl;

      img.onload = () => {
        // Initial style without background color
        const newSectionStyle = {
          backgroundColor: theme?.backgroundImage ? '#cacbd4' : theme?.primary,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          color: theme?.text,
          // transition: 'background 0.3s ease-in-out',
        };
        setSectionStyle(newSectionStyle);

        setTimeout(() => {
          console.log('loaded!');
          setIsBackgroundLoaded(true);
        }, 300);
      };

      img.onerror = () => {
        // Fallback to primary color if image fails to load
        const fallbackStyle = {
          backgroundColor: theme?.primary,
          color: theme?.text,
        };
        setSectionStyle(fallbackStyle);
        console.log('img error');
        setIsBackgroundLoaded(true);
      };
    }, [theme, isMobile]);

    const overflowItems = items && items.length > 3;

    // if (isCheckoutDataLoading || !isBackgroundLoaded) {
    //   return <DescriptionSkeleton isMobile={isMobile} sectionStyle={sectionStyle} />;
    // }

    // Supercell layout render
    if (IS_SUPERCELL_TEMPLATE) {
      if (isCheckoutDataLoading || !isBackgroundLoaded) {
        return (
          <div className="description" style={sectionStyle} ref={ref}>
            <DescriptionSkeleton isMobile={isMobile} sectionStyle={sectionStyle} />
          </div>
        );
      }
      return <SummaryLayout ref={ref} />;
    }

    // Default layout render
    return (
      <div className="description" style={sectionStyle} ref={ref}>
        {isBackgroundLoaded && theme?.backgroundImage && (
          <motion.img
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0 }}
            className="bg-image"
            src={isMobile ? theme?.mobileBackgroundImage : theme?.backgroundImage}
            alt=""
            // style={{
            //   position: 'absolute',
            //   top: 0,
            //   left: 0,
            //   width: '100%',
            //   height: '100%',
            //   objectFit: 'cover',
            //   objectPosition: 'center top',
            //   zIndex: 1,
            // }}
          />
        )}
        {isCheckoutDataLoading || !isBackgroundLoaded ? (
          <DescriptionSkeleton isMobile={isMobile} sectionStyle={sectionStyle} />
        ) : (
          <div className="description-main">
            <div className="description-top">
              {theme?.logo ? (
                <div className="logo-container">
                  <img className="logo" src={theme?.logo} alt="" />
                </div>
              ) : (
                isMobile && <Box height={'16px'} />
              )}
              {isSubscription && <SubscriptionsDescription theme={theme} isMobile={isMobile} />}

              {!isSubscription && (
                <Stack
                  className="description-top-content"
                  display={'flex'}
                  direction={'row'}
                  justifyContent={'space-between'}
                >
                  <Box overflow={'hidden'}>
                    <span className="description-top-title" title={offerDisplayName}>
                      {offerDisplayName}
                    </span>
                    <Box className="description-top-price">
                      <FormattedPrice
                        pricing={pricing}
                        priceValue={priceTotal}
                        data-testid="total-price-header"
                      />
                    </Box>
                  </Box>
                  {showDetailsSection && (
                    <Box
                      display={'flex'}
                      alignItems={'center'}
                      alignSelf={'self-start'}
                      height={16}
                      fontSize={12}
                      onClick={handleExpandClick}
                    >
                      <span>{t('details')}</span>
                      <Box pl={0.5} display={'flex'} alignItems={'center'}>
                        {expanded ? <FaCaretUp /> : <FaCaretDown />}
                      </Box>
                    </Box>
                  )}
                </Stack>
              )}
            </div>
            {!isSubscription && (
              <>
                {((!isMobile && isOfferItemsExists) ||
                  (isMobile && !isMobileCollapsedBreakdown)) && (
                  <Box mt={2}>
                    <hr style={{ borderColor: theme?.text, marginBottom: 0 }} />
                  </Box>
                )}
                <Collapse in={expanded || !isMobile}>
                  {isMobile && isMobileCollapsedBreakdown && isOfferItemsExists && (
                    <Box mt={2}>
                      <hr style={{ borderColor: theme?.text, marginBottom: 0 }} />
                    </Box>
                  )}
                  <div className={`description-body ${overflowItems ? 'overflow-items' : ''}`}>
                    <div className="description-products">
                      {items?.map((item) => (
                        <OfferItem
                          key={item.name}
                          itemName={item.displayName || item.name}
                          itemAmount={item.quantityDisplay || item.quantity}
                          itemImage={item.assetUrl}
                        />
                      ))}
                    </div>
                  </div>
                  <hr style={{ borderColor: theme?.text, marginTop: 0 }} />
                  {isMobile && isMobileCollapsedBreakdown && (
                    <PriceBreakdown
                      pricing={pricing}
                      priceNet={priceNet || 0}
                      priceTotal={priceTotal || 0}
                      tax={tax || 0}
                      discount={discount || 0}
                      showTaxValue={showTaxValue}
                      showCouponField={showCouponField}
                      priceBreakdown={priceBreakdown}
                    />
                  )}
                </Collapse>
                {(!isMobile || (isMobile && !isMobileCollapsedBreakdown)) && (
                  <PriceBreakdown
                    pricing={pricing}
                    priceNet={priceNet || 0}
                    priceTotal={priceTotal || 0}
                    tax={tax || 0}
                    discount={discount || 0}
                    showTaxValue={showTaxValue}
                    showCouponField={showCouponField}
                    priceBreakdown={priceBreakdown}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }
);
